import { DecryptedPatient } from '@icure/cardinal-sdk/model.mjs'
import { Button, Radio, RadioChangeEvent } from 'antd'
import React, { useState } from 'react'
import * as XLSX from 'xlsx'
import { format } from 'date-fns'

import { CustomModal } from '../../../common/CustomModal'
import { SpinLoader } from '../../../common/SpinLoader'
import { ExportMode, FormScope, useLazyExportDataQuery } from '../../../../core/api/exportApi'
import './index.less'

interface ModalExportPatientsProps {
  isVisible: boolean
  onClose: () => void
  patients: DecryptedPatient[]
}

type ComponentState = 'configure' | 'exporting'

export const ModalExportPatients = ({ isVisible, onClose, patients }: ModalExportPatientsProps) => {
  const [componentState, setComponentState] = useState<ComponentState>('configure')
  const [exportMode, setExportMode] = useState<ExportMode>('admin')
  const [formScope, setFormScope] = useState<FormScope>('latest')

  const [triggerExport] = useLazyExportDataQuery()

  const handleExport = async () => {
    setComponentState('exporting')

    try {
      const result = await triggerExport({ mode: exportMode, patients, formScope }).unwrap()

      if (result.rows.length > 0) {
        const headers = result.columns.map((c) => c.label)
        const ws = XLSX.utils.json_to_sheet(result.rows, { header: headers })
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Export')
        const dateStr = format(new Date(), 'yyyy-MM-dd')
        XLSX.writeFile(wb, `preventi-${exportMode}-${dateStr}.xlsx`)
      }

      onClose()
    } catch (error) {
      console.error('Export failed:', error)
      setComponentState('configure')
    }
  }

  const customFooter = () => {
    return [
      <div key="customFooter" style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, width: '100%' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="primary" onClick={handleExport} disabled={componentState === 'exporting' || patients.length === 0}>
          Export
        </Button>
      </div>,
    ]
  }

  return (
    <CustomModal isVisible={isVisible} handleClose={onClose} customFooter={customFooter()} title="Export patients">
      <div className="modalExportPatients">
        {componentState === 'exporting' && <SpinLoader />}
        {componentState === 'configure' && (
          <>
            <p className="modalExportPatients__info">
              {patients.length} {patients.length === 1 ? 'patient' : 'patients'} will be exported.
            </p>
            <div className="modalExportPatients__radioGroup">
              <p className="modalExportPatients__radioGroup__label">Export mode</p>
              <Radio.Group value={exportMode} onChange={(e: RadioChangeEvent) => setExportMode(e.target.value)}>
                <Radio value="admin">Administrative data</Radio>
                <Radio value="formData">Form data (questionnaire answers)</Radio>
              </Radio.Group>
            </div>
            {exportMode === 'formData' && (
              <div className="modalExportPatients__radioGroup">
                <p className="modalExportPatients__radioGroup__label">Form scope</p>
                <Radio.Group value={formScope} onChange={(e: RadioChangeEvent) => setFormScope(e.target.value)}>
                  <Radio value="latest">Latest consultation only</Radio>
                  <Radio value="all">All consultations</Radio>
                </Radio.Group>
              </div>
            )}
          </>
        )}
      </div>
    </CustomModal>
  )
}
